import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * MobileBottomSheet (issue #26 Phase 7-2)
 *
 * 汎用 bottom sheet コンポーネント。 spec §2.2.1 / plan §3.2 通り、
 * 既存 EditorPanel bottom sheet (FingeringEditorPageMobile.tsx) のパターンを踏襲。
 *
 * - 常にレンダリング、 open=false 時は `translate-y-full` で隠す (slide-out アニメ用に DOM 残置)
 * - overlay は `open` 条件で出し入れ (overlay 自体はアニメなしで OK)
 * - z 階層: overlay z-20 / sheet 本体 z-30 (Phase 5b sticky bar z-25 と整合)
 * - role="dialog" + aria-modal="true" で a11y 担保
 * - Escape キー / overlay クリックで onClose
 * - issue #90: createPortal(document.body) で body 直下へ出す。 EditorToolbar の backdrop-filter
 *   祖先は position:fixed の containing block を作るため、 中で render すると fixed が viewport
 *   基準にならず画面下に届かない。 portal で祖先から脱出させ fixed を viewport 基準に戻す。
 */
interface MobileBottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
}

const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({ open, onClose, children, ariaLabel }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // issue #90: SSR ガード (CRA SPA だが防御的に)。 document が無ければ何も描画しない。
  if (typeof document === 'undefined') return null;

  // issue #90: backdrop-filter 祖先から脱出させるため body 直下へ portal する。
  // 中身 (overlay / sheet / className / aria / grab handle / アニメ) は一切変えない。
  return createPortal(
    <>
      {open && (
        <div
          data-testid="mobile-bottom-sheet-overlay"
          onPointerDown={onClose}
          className="fixed inset-0 bg-black/40 z-20"
        />
      )}
      <div
        data-testid="mobile-bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`fixed left-0 right-0 bottom-0 bg-slate-800 border-t border-slate-600 rounded-t-[20px] px-5 pt-3 pb-7 z-30 max-h-[65vh] overflow-y-auto
          transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${open ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="flex justify-center mb-3">
          <div data-testid="mobile-bottom-sheet-grab-handle" className="w-9 h-1 rounded-sm bg-slate-600" />
        </div>
        {children}
      </div>
    </>,
    document.body,
  );
};

export default MobileBottomSheet;
